import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { KbChatbotService, ChatMessage } from './kb-chatbot.service';
import { Subject, Subscription, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { HttpErrorResponse } from '@angular/common/http';

const STORAGE_KEY = 'kb-chat-history';
const MAX_SAVED_MESSAGES = 50;

@Component({
  selector: 'app-kb-chatbot',
  templateUrl: './kb-chatbot.component.html',
  styleUrls: ['./kb-chatbot.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class KbChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  isOpen = false;
  question = '';
  isLoading = false;
  messages: ChatMessage[] = [];

  private askSubject = new Subject<{ question: string; page: string }>();
  private askSubscription?: Subscription;
  private shouldScroll = false;

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  constructor(
    private readonly chatbotService: KbChatbotService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // Load persisted chat history
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        this.messages = JSON.parse(saved);
      }
    } catch {
      // Corrupted data or private browsing mode
    }

    // Set up switchMap pipeline with catchError inside pipe (prevents observable termination)
    this.askSubscription = this.askSubject
      .pipe(
        switchMap(({ question, page }) =>
          this.chatbotService.ask(question, page).pipe(
            catchError((err: HttpErrorResponse) => {
              this.handleError(err);
              return EMPTY;
            })
          )
        )
      )
      .subscribe(response => {
        this.messages.push({
          role: 'assistant',
          content: response.answer,
          sources: response.sources,
          followUp: response.followUp,
          confidence: response.confidence
        });
        this.isLoading = false;
        this.shouldScroll = true;
        this.persistMessages();
      });
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.messages.length === 0) {
      this.addWelcomeMessage();
    }
  }

  renderMarkdown(text: string): SafeHtml {
    const rawHtml = marked.parse(text, { async: false }) as string;
    const clean = DOMPurify.sanitize(rawHtml);
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  async ask(): Promise<void> {
    const q = this.question.trim();
    if (!q || this.isLoading) return;

    this.messages.push({ role: 'user', content: q, sources: [], followUp: [] });
    this.question = '';
    this.isLoading = true;
    this.shouldScroll = true;
    this.persistMessages();

    // Reset textarea height after sending
    const textarea = document.querySelector('.kb-chatbot-input textarea') as HTMLTextAreaElement;
    if (textarea) textarea.style.height = 'auto';

    this.askSubject.next({ question: q, page: window.location.pathname });
  }

  onEnterKey(event: Event): void {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.shiftKey) return; // Shift+Enter = new line
    keyEvent.preventDefault();
    this.ask();
  }

  autoGrow(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  }

  askFollowUp(question: string): void {
    this.question = question;
    this.ask();
  }

  clearChat(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.messages = [];
    this.addWelcomeMessage();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.askSubscription?.unsubscribe();
  }

  private addWelcomeMessage(): void {
    this.messages.push({
      role: 'assistant',
      content:
        "Hi! I'm Dashboardly's AI assistant. Ask me anything about your analytics dashboard.",
      sources: [],
      followUp: [
        'What metrics does Dashboardly track?',
        'How do I read the Profit & Loss page?',
        'What is COGS and how is it calculated?'
      ]
    });
    this.persistMessages();
  }

  private persistMessages(): void {
    try {
      const toSave = this.messages.slice(-MAX_SAVED_MESSAGES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch {
      // Private browsing or quota exceeded
    }
  }

  private handleError(error: HttpErrorResponse): void {
    let message: string;
    if (error.status === 0) {
      message = 'Connection error. Please check your internet and try again.';
    } else if (error.status === 429) {
      message = 'Too many questions. Please wait a moment and try again.';
    } else if (error.status === 503) {
      message = 'Knowledge base is temporarily unavailable. Please try again later.';
    } else {
      message = "Sorry, I couldn't process your question. Please try again.";
    }
    this.messages.push({
      role: 'assistant',
      content: message,
      sources: [],
      followUp: [],
      isError: true
    });
    this.isLoading = false;
    this.shouldScroll = true;
    this.persistMessages();
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch {}
  }
}
